import { Input, Selector } from '@casumo/dojo-react';
import { ChevronDown, Close, Search } from '@casumo/dojo-react-icons';
import { memo, useMemo, useState } from 'react';
import useCommonTranslations from '../api/hooks/useCommonTranslations.tsx';
import InfoGlyph from '../assets/icons/infoGlyph.tsx';
import Drawer from '../design-system/drawer.tsx';
import Tooltip from '../design-system/tooltip.tsx';
import { useRegistrationStore } from '../store/store.ts';
import { TProvinceSelectorField } from '../types/types.ts';
import { DISABLED_PROVINCE_TOOLTIP, TEST_ID } from '../utils/constants.ts';
import { CA_PROVINCES, isDisabledProvince } from '../utils/countries.ts';
import { GTM_ERRORS, GTM_PAYLOAD } from '../utils/gtm/constants.ts';
import { GtmEvents } from '../utils/gtm/gtm.ts';

const ProvinceSelector = ({ name, dialogTitle, validation, onBlur }: Partial<TProvinceSelectorField> & {
  name: string;
  onChange?: (e: string) => void;
  onBlur?: (field: Partial<TProvinceSelectorField>) => void;
}) => {
  const { formData, setFormData, metadata, setMetadata, setErrors, errors } = useRegistrationStore();
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const {
    commonTranslations,
    isLoading,
  } = useCommonTranslations();

  const handleIsOpen = (isOpen: boolean) => {
    setIsOpen(isOpen);
  };

  const filteredProvinces = useMemo(
    () => CA_PROVINCES.filter(item => (item.name).toLowerCase().includes(searchValue.toLowerCase())),
    [searchValue],
  );

  const placeholder = isLoading ? '' : commonTranslations?.search ?? 'Search'

  const logUnsupportedProvince = () => {
    GtmEvents.logAlert({
      step:       metadata?.currentStep,
      alert:      GTM_ERRORS.UNSUPPORTED_PROVINCE,
      userStatus: GTM_PAYLOAD.LOGGED_OUT,
    });
  };

  const provinceName = CA_PROVINCES.find(province => province.code === formData?.[name])?.name ?? '';

  const readOnlyInput = () => (
    <Input
      id="countrySelectorInput"
      name={name}
      label="Select province"
      value={provinceName}
      onKeyDown={e => e.key === 'Enter' && handleIsOpen(true)}
      readOnly
      sufix={<span className="text-[24px]"><ChevronDown /></span>}
      onClick={() => handleIsOpen(true)}
      onBlur={() => onBlur?.({ name, validation })}
      errorMessage={errors[name]}
      error={errors[name] ? true : undefined}
      data-error={errors[name] ? true : undefined}
      data-testid={TEST_ID.COMBOBOX_TRIGGER + name}
    />
  );

  return (
    <>
      {readOnlyInput()}
      <Drawer isOpen={isOpen} setIsOpen={setIsOpen}>
        <div className="flex flex-col h-full w-full p-md overflow-hidden">
          <div className="flex justify-between text-white pb-sm font-bold">
            <h1 className="text-h-lg">{dialogTitle}</h1>
            <span className="p-2xs cursor-pointer text-[24px]" onClick={() => handleIsOpen(false)}><Close /></span>
          </div>
          <Input
            id="countrySearch"
            name="search"
            placeholder={placeholder}
            variant="dark"
            onChange={(e) => setSearchValue(e.currentTarget.value)}
            value={searchValue}
            prefix={<span className="text-[24px]"><Search /></span>}
            onClear={() => setSearchValue('')}
            clearable
            autoFocus
            data-testid={TEST_ID.SEARCH + name}
          />
          <Selector name="province" direction="vertical" variant="dark"
            className="flex-1 w-full mt-md p-0.5 overflow-y-auto">
            {filteredProvinces.map(province => (
              isDisabledProvince(province.code) ? (
                <Tooltip
                  key={province.code}
                  icon={<InfoGlyph />}
                  content={DISABLED_PROVINCE_TOOLTIP}
                  className="flex gap-3xs relative w-full h-fit text-body-base-bold p-sm rounded-lg
                    bg-purple-80 text-white opacity-50 cursor-not-allowed"
                  tabIndex={0}
                  aria-disabled="true"
                  onClick={logUnsupportedProvince}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      logUnsupportedProvince();
                    }
                  }}
                  data-testid={TEST_ID.PROVINCE + province.name}
                >
                  {province.name}
                </Tooltip>
              ) : (
                <Selector.Radio
                  key={province.code}
                  className="flex gap-3xs"
                  value={province.code}
                  onClick={() => {
                    setErrors({ [name]: '' });
                    setFormData({ [name]: province.code });
                    setMetadata({ selectedProvince: province.name, selectedProvinceCode: province.code });
                    setIsOpen(false);
                  }}
                  onKeyDown={e => e.key === 'Enter' && setFormData({ [name]: province })}
                  data-testid={TEST_ID.PROVINCE + province.name}
                >
                  {province.name}
                </Selector.Radio>
              )
            ))}
          </Selector>
        </div>
      </Drawer>
    </>
  );
};

const Memoized = memo(ProvinceSelector);
Memoized.displayName = 'ProvinceSelector';

export default Memoized;
